import * as _ from 'lodash';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthenticatedUserService } from 'services/authenticatedUser';
import { Branch, OrganizationModel } from 'models';
import { BranchSettings, BranchToCreate } from 'models/organization.model';
import { DropdownTreeItem } from 'fc-ui-components';

@Injectable({ providedIn: 'root' })
export class StoreService implements OnDestroy {
    public stores: BehaviorSubject<Branch[]> = new BehaviorSubject([]);
    public storesIncludingDisabled: BehaviorSubject<Branch[]> = new BehaviorSubject([]);
    private authenticatedUserSubscriber: Subscription;
    public storeDropdown: Observable<DropdownTreeItem<Branch>[]>;
    public hasError: BehaviorSubject<boolean>;

    constructor(
        private authenticatedUserService: AuthenticatedUserService,
        private organizationModel: OrganizationModel
    ) {
        this.storeDropdown = this.stores.pipe(
            map((stores) =>
                stores.map((store) => {
                    const { address, name, shortName } = store;
                    const { address1, zip, city } = address;

                    let label = '';

                    if (shortName) {
                        label = shortName;
                    } else if (name) {
                        label = name;
                    } else {
                        label = `${address1}, ${zip} ${city}`;
                    }

                    return { value: store, label };
                })
            )
        );

        this.authenticatedUserSubscriber = this.authenticatedUserService.isLoggedIn.subscribe((isLoggedIn) => {
            if (isLoggedIn) {
                this.pullStores();
            } else {
                this.stores.next([]);
            }
        });

        this.hasError = new BehaviorSubject<boolean>(false);
    }

    ngOnDestroy() {
        this.authenticatedUserSubscriber.unsubscribe();
    }

    public resetHasError(): void {
        this.hasError.next(false);
    }

    public updateStore(store: BranchToCreate, gln: string) {
        this.hasError.next(false);
        const organizationId = this.authenticatedUserService.user.value.organization._id;
        return this.organizationModel
            .putBranch(organizationId, gln, store)
            .then(() => this.pullStores())
            .catch((error) => {
                console.error(`Error updating store with GLN ${gln}`, error);
                this.hasError.next(true);
                throw error;
            });
    }

    public setStoreSettings(store: BranchSettings, gln: string) {
        this.hasError.next(false);
        const organizationId = this.authenticatedUserService.user.value.organization._id;
        return this.organizationModel
            .patchBranchSettings(organizationId, gln, store)
            .then(() => this.pullStores())
            .catch((error) => {
                console.error(`Error updating store settings for GLN ${gln}`, error);
                this.hasError.next(true);
                throw error;
            });
    }

    public updateStoreClaraContacts(storeWithUpdatedContacts: Branch, gln: string) {
        const organizationId = this.authenticatedUserService.user.value.organization._id;
        const useFcGln = storeWithUpdatedContacts.address.fcGln
            ? _.isEqual(storeWithUpdatedContacts.address.gln, storeWithUpdatedContacts.address.fcGln)
            : false;
        const updatedStore = {
            ..._.omit(storeWithUpdatedContacts, 'users'),
            useFcGln
        };

        return this.organizationModel.putBranch(organizationId, gln, updatedStore).then(() => this.pullStores());
    }

    public updateStoreClaraUsers(gln: string, claraUser: any) {
        const organizationId = this.authenticatedUserService.user.value.organization._id;
        return this.organizationModel.createBranchUser(organizationId, gln, claraUser).then(() => this.pullStores());
    }

    public deleteStoreClaraUser(gln: string, claraUsername: string) {
        const organizationId = this.authenticatedUserService.user.value.organization._id;
        return this.organizationModel
            .deleteBranchUser(organizationId, gln, claraUsername)
            .then(() => this.pullStores());
    }

    public createStore(store: BranchToCreate) {
        this.hasError.next(false);
        const organizationId = this.authenticatedUserService.user.value.organization._id;
        return this.organizationModel
            .createBranch(organizationId, store)
            .then(() => this.pullStores())
            .catch((error) => {
                console.error(`Error creating store with GLN ${store.address.gln}`, error);
                this.hasError.next(true);
                throw error;
            });
    }

    public deleteStore(gln: string) {
        this.hasError.next(false);
        const organizationId = this.authenticatedUserService.user.value.organization._id;
        return this.organizationModel
            .deleteBranchByGln(organizationId, gln)
            .then((store) => {
                this.pullStores();
                return store;
            })
            .catch((error) => {
                console.error(`Error deleting store with GLN ${gln}`, error);
                this.hasError.next(true);
                throw error;
            });
    }

    public pullStores() {
        const organizationId = this.authenticatedUserService.user.value.organization._id;
        return this.organizationModel
            .getBranches(organizationId)
            .then((stores) => {
                if (_.isEmpty(stores)) {
                    this.storesIncludingDisabled.next([]);
                    this.stores.next([]);
                    return;
                }
                this.storesIncludingDisabled.next(stores);
                this.stores.next(stores.filter(({ isEnabled }) => isEnabled));
            })
            .catch((error) => {
                console.error(`Stores could not be loaded`, error);
                this.stores.next([]);
            });
    }

    public fetchStoresObservable() {
        const organizationId = this.authenticatedUserService.user.value.organization._id;
        return this.organizationModel
            .getBranchesAsObservable(organizationId)
            .pipe(map((stores) => stores.filter((branch) => branch.isEnabled)));
    }
}
